/**
 * Forward-only schema migrations of document snapshots (W1-03 core-model-ops; ARCHITECTURE.md §10).
 *
 * `SerializedChartDoc.version` names the schema of the snapshot. `MIGRATIONS[v]` upgrades a version-v snapshot to
 * version v + 1; `migrateSnapshot` runs the chain up to `CURRENT_DOC_VERSION`. Version 1 is the baseline, so the
 * chain is empty today: the next schema change adds `MIGRATIONS[1]` (and a test that a v1 snapshot loads).
 * Snapshots from a NEWER editor are refused (never downgraded or partially read).
 */

import { formatModelMessage, type MessageParams } from '../model/messageFormat';
import { isPlainObject } from '../util/json';

/** Schema version written by this build. */
export const CURRENT_DOC_VERSION = 1;

/**
 * A problem found while reading a snapshot: where (e.g. `keys[12].ease.left`) and what. `code` is the full catalogue
 * key (`model.shape.problem`, `model.snapshot.newer`...) and `params` its language-neutral parameters, so the UI can
 * show the problem in any locale (`i18n.tr({ key: code, params })`); `problem` is the English text.
 */
export interface DocFormatProblem {
  readonly path: string;
  readonly problem: string;
  readonly code: string;
  readonly params: MessageParams;
}

/** A DocFormatProblem from a 'model' catalogue key (without the namespace) and its parameters. */
export function docFormatProblem(path: string, key: string, params: MessageParams = {}): DocFormatProblem {
  return { path, problem: formatModelMessage(key, params), code: `model.${key}`, params };
}

/** Corrupt or unreadable snapshot. `path` / `problem` describe the first problem; `problems` lists them all. */
export class DocFormatError extends Error {
  override readonly name = 'DocFormatError';
  readonly path: string;
  readonly problem: string;
  readonly problems: readonly DocFormatProblem[];

  constructor(problems: readonly DocFormatProblem[], truncated = false) {
    const first = problems[0] ?? docFormatProblem('(root)', 'snapshot.unreadable');
    const more = problems.length - 1;
    const tail = more > 0 ? ` (and ${String(more)}${truncated ? '+' : ''} more problem${more === 1 ? '' : 's'})` : '';
    super(`Invalid document snapshot at ${first.path}: ${first.problem}${tail}`);
    this.path = first.path;
    this.problem = first.problem;
    this.problems = problems;
  }
}

type Snapshot = Readonly<Record<string, unknown>>;
/** One schema step: a version-v snapshot to version v + 1 (pure; must not mutate its input). */
export type Migration = (snapshot: Readonly<Record<string, unknown>>) => Readonly<Record<string, unknown>>;

/** MIGRATIONS.get(v): version v -> v + 1. Empty while version 1 is the only schema. */
const MIGRATIONS: ReadonlyMap<number, Migration> = new Map<number, Migration>();

/** Version of a snapshot-like value, or throws a DocFormatError. */
export function snapshotVersion(json: unknown): number {
  if (!isPlainObject(json)) throw new DocFormatError([docFormatProblem('(root)', 'snapshot.notObject')]);
  if (json.kind !== 'phiedit-doc') throw new DocFormatError([docFormatProblem('kind', 'snapshot.kind')]);
  const v = json.version;
  if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 1) {
    throw new DocFormatError([docFormatProblem('version', 'snapshot.version')]);
  }
  return v;
}

/**
 * Runs the chain `migrations` from the snapshot's version up to `target` (each step's output gets
 * `version: v + 1`). Refuses snapshots newer than `target` and gaps in the chain.
 */
export function runMigrations(json: unknown, migrations: ReadonlyMap<number, Migration>, target: number): unknown {
  let v = snapshotVersion(json);
  if (v > target) {
    throw new DocFormatError([docFormatProblem('version', 'snapshot.newer', { version: v, supported: target })]);
  }
  let cur = json as Snapshot;
  while (v < target) {
    const step = migrations.get(v);
    if (step === undefined) {
      throw new DocFormatError([docFormatProblem('version', 'snapshot.noMigration', { version: v })]);
    }
    cur = { ...step(cur), version: v + 1 };
    v++;
  }
  return cur;
}

/** Upgrades a snapshot to `CURRENT_DOC_VERSION` (returns the input itself when it is current). */
export function migrateSnapshot(json: unknown): unknown {
  return runMigrations(json, MIGRATIONS, CURRENT_DOC_VERSION);
}
